import { ChangeDetectionStrategy, Component, Input, OnDestroy, type OnInit } from '@angular/core';

import { Subscription } from 'rxjs';

import { WindowRef } from '@fcom/core/providers/window';
import { entrySet } from '@fcom/core/utils';
import { ButtonMode, ButtonSize, ButtonTheme } from '@fcom/ui-components';
import { ConfigService, SentryLogger } from '@fcom/core/services';

import { ClaimOrFeedbackService } from '../../../services/claim-or-feedback.service';
import {
  CategoryMapLevel1,
  CategoryMapLevel2,
  MedaCategories,
  MessageAttachment,
  type CaseGetMessage,
  type RetrieveCase,
} from '../../../interfaces';
import { formatUserInput, getFileExtensionFromMimeType } from '../../../utils';

const categoryToTranslationKey = (category = ''): string => {
  const values =
    [CategoryMapLevel2.overbooking, CategoryMapLevel2.downgrading].indexOf(category) !== -1
      ? entrySet(CategoryMapLevel2)
      : entrySet(CategoryMapLevel1);
  const regularValues = values.filter((value) => !MedaCategories[value.key]);
  const regularCategory = regularValues.find((reg) => reg.value === category);

  if (regularCategory) {
    return `SERVICE_FORMS.CSServiceForms.wizard.steps.formCategories.formSubCategories.${regularCategory.key}.title`;
  }

  const medaValues = values.filter((value) => !!MedaCategories[value.key]);
  const medaCategory = medaValues.find((meda) => meda.value === category);

  if (medaCategory) {
    return `SERVICE_FORMS.NTServiceForms.fields.reasonForSpecialAttention.${medaCategory.key}`;
  }

  return '';
};

@Component({
  selector: 'fin-retrieve-case',
  templateUrl: './retrieve-case.component.html',
  styleUrls: ['./retrieve-case.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RetrieveCaseComponent implements OnInit, OnDestroy {
  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonMode = ButtonMode;
  readonly isNewDownloadAttachmentFlowEnabled: boolean;

  @Input()
  responseContent: RetrieveCase;

  componentCase: RetrieveCase & { categoryKey: string };
  messageAmount = 3;
  attachmentBaseUrl: string;
  private readonly subscriptions: Subscription = new Subscription();

  constructor(
    private readonly claimOrFeedbackService: ClaimOrFeedbackService,
    private readonly configService: ConfigService,
    private readonly windowRef: WindowRef,
    private readonly sentryLogger: SentryLogger
  ) {
    this.isNewDownloadAttachmentFlowEnabled = this.configService.cfg.enableNewDownloadAttachmentFlow;
    const { apiUrl } = this.configService.cfg.serviceForm.customerFeedback;
    const attachmentPath: string =
      this.configService.cfg.serviceForm.customerFeedback?.options?.['attachmentPath'] || 'attachment';
    this.attachmentBaseUrl = `${apiUrl}/${attachmentPath}/`;
  }

  ngOnInit(): void {
    this.componentCase = {
      ...this.responseContent,
      categoryKey: categoryToTranslationKey(
        this.responseContent.category?.child?.name ?? this.responseContent.category?.name
      ),
      messages: this.responseContent.messages?.map(this.formatMessage) ?? [],
    };
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  handleNewMessage(message: CaseGetMessage): void {
    this.componentCase.messages = [...this.componentCase.messages, this.formatMessage(message)];

    // Update the message amount if not initial
    if (this.messageAmount !== 3) {
      this.messageAmount = this.componentCase.messages.length;
    }
  }

  private formatMessage(caseMessage: CaseGetMessage): CaseGetMessage {
    let content: string = formatUserInput(caseMessage.content);
    // Remove empty paragraphs
    content = content.replace(/<p>\s*<\/p>/g, '');
    // Remove leading and trailing spaces from paragraphs
    content = content.replace(/<p>\s/g, '<p>');
    content = content.replace(/\s<\/p>/g, '</p>');
    return { ...caseMessage, content };
  }

  downloadAttachment(attachment: MessageAttachment): void {
    this.subscriptions.add(
      this.claimOrFeedbackService.downloadAttachment(attachment.attachmentId, attachment.hash).subscribe({
        next: (blob) => {
          const fileName: string = (attachment.fileName || 'download') + getFileExtensionFromMimeType(blob.type);
          this.triggerFileDownload(blob, fileName);
        },
        error: (error) => {
          this.sentryLogger.warn('Case message attachment download has failed.', { error });
        },
      })
    );
  }

  triggerFileDownload(blob: Blob, fileName: string): void {
    const url: string = URL.createObjectURL(blob);
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 5000);
    const a = this.windowRef.nativeWindow.document.createElement('a');
    a.href = url;
    a.download = fileName;
    this.windowRef.nativeWindow.document.body.appendChild(a);
    a.click();
    this.windowRef.nativeWindow.document.body.removeChild(a);
  }
}
